import { BookingOrder, CartItem, DrugItem, ProposalItem } from 'src/api/types';
import { AnalyticEvent } from 'src/api/analytic.types';
import useCartStore from 'src/hooks/useCart';
import logger from 'src/utils/logger';
import useLocationStore from '../useLocationStore';
import useAuthorizationStore from '../useAuthorizationStore';
import { EMPTY_STRING } from './useAnalytics.contants';
import { ChangeCartItemsType, DrugAnalyticType } from './useAnalytics.types';
import {
  sendAnalytics,
  getPackageFullness,
  getEcommerceItems
} from './useAnalytics.helpers';
import useStoreDeep from '../useStore';
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable import/prefer-default-export */

// All method with additional logic for sendAnalytics should be covered with trycatch
// Because:
// 1. most of the methods rely on cookies-stored fields whic could be absent due to outdated user cookies
// 2. all of the methods are integrated with 3rd-party analytics which might work unexpected
// Any crashes in these methods shouldn't lead to whole app crash
export const useAnalytics = () => {
  const userLocation = useStoreDeep(
    useLocationStore,
    (state) => state.userLocation
  );
  const userToken = useStoreDeep(
    useAuthorizationStore,
    (state) => state.userToken
  );
  const simpleCartItems = useStoreDeep(
    useCartStore,
    (state) => state.simpleCartItems
  );
  const simpleCartItem = useStoreDeep(
    useCartStore,
    (state) => state.simpleCartItem
  );

  const city = userLocation?.name ?? EMPTY_STRING;

  const sendViewItemAnalytic = (drug: DrugAnalyticType) => {
    try {
      if (city === EMPTY_STRING) {
        return;
      }
      sendAnalytics({
        event: AnalyticEvent.ViewItem,
        city,
        ecommerce: {
          items: [
            {
              item_name: drug.name,
              item_id: drug.id,
              price: drug.minPrice,
              item_brand: drug.maker,
              item_category: drug.category,
              item_list_name: drug.analytics?.itemListName,
              index: drug.analytics?.index
            }
          ]
        }
      });
    } catch (error) {
      logger.error(error);
    }
  };

  const sendChangeCartItemsAnalytic = ({
    drug: { name, id, maker, category, minPrice, analytics, translitName },
    packageQuantityDetails,
    isRemove = false
  }: ChangeCartItemsType) => {
    try {
      let currentAnalytic = analytics;

      if (!currentAnalytic) {
        const simpleCartIDrug = simpleCartItems?.find(
          (item) => item.translitName === translitName
        );
        currentAnalytic = simpleCartIDrug?.analytics;
      }

      sendAnalytics({
        event: isRemove
          ? AnalyticEvent.RemoveFromCart
          : AnalyticEvent.AddToCart,
        city,
        package: getPackageFullness(packageQuantityDetails),
        ecommerce: {
          items: getEcommerceItems(
            {
              name,
              id,
              maker,
              category,
              price: minPrice,
              analytics: currentAnalytic
            },
            packageQuantityDetails
          )
        }
      });
    } catch (error) {
      logger.error(error);
    }
  };

  const sendSelectItemAnalytic = (drug: DrugItem) => {
    try {
      sendAnalytics({
        event: AnalyticEvent.SelectItem,
        city,
        ecommerce: {
          items: [
            {
              item_name: drug.name,
              item_id: drug.id,
              price: drug.minPrice,
              item_brand: drug.maker,
              item_category: drug.category,
              item_list_name: drug.analytics?.itemListName,
              index: drug.analytics?.index
            }
          ]
        }
      });
    } catch (error) {
      logger.error(error);
    }
  };

  const sendViewCartAnalytic = (cartItems: CartItem[]) => {
    try {
      if (cartItems?.length !== simpleCartItems?.length) {
        return;
      }
      const items = cartItems
        .map(
          ({
            drug: { name, id, maker, category, minPrice, translitName },
            ...rest
          }) => {
            const { analytics } = simpleCartItems?.find(
              (item) => item.translitName === translitName
            ) ?? { analytics: null };
            if (!analytics) {
              logger.error('error: analytics do not exist');
              return null;
            }
            return getEcommerceItems(
              { name, id, maker, category, price: minPrice, analytics },
              rest
            );
          }
        )
        .flat()
        .filter((item) => !!item);
      if (items?.length) {
        sendAnalytics({
          event: AnalyticEvent.ViewCart,
          city,
          ecommerce: { items }
        });
      }
    } catch (error) {
      logger.error(error);
    }
  };

  const sendBeginCheckoutAnalytic = (
    cartItems: CartItem[],
    proposalItem: ProposalItem | null
  ) => {
    try {
      if (!proposalItem) {
        return;
      }
      const items = cartItems
        .map(({ drug, ...rest }, index) => {
          const simpleCartItemList = simpleCartItem
            ? [simpleCartItem]
            : simpleCartItems;

          const { analytics } = simpleCartItemList?.find(
            ({ translitName }) => translitName === drug.translitName
          ) ?? { analytics: null };

          if (!analytics) {
            logger.error('error: analytics do not exist');
            return null;
          }

          return getEcommerceItems(
            {
              ...drug,
              analytics,
              price: proposalItem?.rests[index]?.priceReserve ?? 0
            },
            rest
          );
        })
        .flat()
        .filter((item) => !!item);
      if (items?.length) {
        sendAnalytics({
          event: AnalyticEvent.BeginCheckout,
          city,
          pharmacy: proposalItem?.partnerName,
          ecommerce: { items }
        });
      }
    } catch (error) {
      logger.error(error);
    }
  };

  const sendPurchaseAnalytic = (savedOrder: BookingOrder) => {
    try {
      const items = savedOrder.items
        .map(({ price, drug, packQuantity, unitQuantity }) => {
          const { name, id, maker, category, quantityUnitInPack } = drug;
          const simpleCartItemList = simpleCartItem
            ? [simpleCartItem]
            : simpleCartItems;
          const { analytics } = simpleCartItemList?.find(
            ({ translitName }) => translitName === id
          ) ?? { analytics: null };
          if (!analytics) {
            logger.error('error: analytics do not exist');
            return null;
          }

          return getEcommerceItems(
            {
              name,
              id,
              price,
              maker,
              category,
              analytics
            },
            { packQuantity, unitQuantity, quantityUnitInPack }
          );
        })
        .flat()
        .filter((item) => !!item);
      if (items?.length) {
        sendAnalytics({
          event: AnalyticEvent.Purchase,
          city,
          pharmacy: savedOrder.branchBrand,
          ecommerce: {
            transaction_id: savedOrder.code,
            affiliation: userToken ? 'login' : 'not login',
            value: savedOrder.price,
            currency: 'UAH',
            coupon: savedOrder.promoCode,
            items
          }
        });
      }
    } catch (error) {
      logger.error(error);
    }
  };

  const sendCartBookConfirmDoneBookingsAnalytic = (order: BookingOrder) =>
    sendAnalytics({
      event: AnalyticEvent.CartBookConfirmDoneBookings,
      transaction_id: order.code,
      affiliation: userToken ? 'login' : 'not login',
      city,
      pharmacy: order.branchBrand
    });

  const sendCityAnalytic = (locationName: string) =>
    sendAnalytics({
      event: AnalyticEvent.ChangeCity,
      city: locationName
    });

  const sendLoginAnalytic = (uid: string) =>
    sendAnalytics({
      event: AnalyticEvent.Login,
      uid
    });

  return {
    sendCartBookConfirmDoneBookingsAnalytic,
    sendChangeCartItemsAnalytic,
    sendBeginCheckoutAnalytic,
    sendSelectItemAnalytic,
    sendViewCartAnalytic,
    sendViewItemAnalytic,
    sendPurchaseAnalytic,
    sendCityAnalytic,
    sendLoginAnalytic
  };
};
